import type { DialogProps } from '@mui/material/Dialog';
import type { NonCompliantAppointmentFeedback } from 'src/utils/non-compliant-appointment-approval';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';

type Props = {
  open: boolean;
  feedback: NonCompliantAppointmentFeedback[];
  onClose: () => void;
  onConfirm: (reason: string) => void;
  confirming?: boolean;
} & Omit<DialogProps, 'open' | 'onClose'>;

export function NonCompliantApprovalDialog({
  open,
  feedback,
  onClose,
  onConfirm,
  confirming = false,
  ...other
}: Props) {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const trimmedReason = reason.trim();

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      {...other}
      container={typeof document !== 'undefined' ? document.body : undefined}
      disableEnforceFocus
      disableAutoFocus
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <DialogTitle>{t('nonCompliantApproval.title')}</DialogTitle>
      <DialogContent sx={{ overflow: 'visible', pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('nonCompliantApproval.message')}
        </Typography>
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {feedback.map((item, index) => (
            <Stack
              key={`${item.code}-${index}`}
              direction="row"
              alignItems="flex-start"
              gap={0.5}
            >
              <Iconify
                icon="material-symbols:info-outline"
                color={item.type === 'error' ? 'error.main' : 'warning.main'}
              />
              <Typography
                variant="body2"
                color={item.type === 'error' ? 'error.main' : 'warning.main'}
              >
                {t(`feedbackCodes.${item.code}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Box>
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={3}
            id="non-compliant-approval-reason"
            label={t('nonCompliantApproval.reasonLabel')}
            placeholder={t('nonCompliantApproval.reasonPlaceholder')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            inputProps={{ maxLength: 2000 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={confirming}>
          {t('nonCompliantApproval.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!trimmedReason || confirming}
          onClick={() => onConfirm(trimmedReason)}
        >
          {t('nonCompliantApproval.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
